import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, X, Check, Info } from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { oauthService } from '@/services/oauthService';
import { useOAuth } from '@/hooks/useOAuth';
import { useOAuthPersistence } from '@/hooks/useOAuthPersistence';

interface DiscordConnectionProps {
    connected: boolean;
    onConnect: (username?: string) => void;
    onDisconnect: () => void;
    loading?: boolean;
    userType?: string;
}

export function DiscordConnection({
    connected,
    onConnect,
    onDisconnect,
    loading = false,
    userType
}: DiscordConnectionProps) {
    const { user } = useAuth();
    const { isConnecting, connect } = useOAuth();
    const { getConnection, disconnect: persistDisconnect } = useOAuthPersistence();
    const [discordProfile, setDiscordProfile] = useState<any>(null);

    useEffect(() => {
        if (getConnection('discord') && !connected) {
            const connection = getConnection('discord');
            if (connection) {
                onConnect(connection.userProfile.username);
            }
        }
    }, [getConnection, onConnect, connected]);

    useEffect(() => {
        if (connected) {
            const connection = getConnection('discord');
            if (connection) {
                setDiscordProfile(connection.userProfile);
            }
        } else {
            setDiscordProfile(null);
        }
    }, [connected, getConnection]);

    useEffect(() => {
        const handleOAuthConnected = (event: CustomEvent) => {
            const { platform, userProfile } = event.detail;
            if (platform === 'discord' && !connected) {
                onConnect(userProfile.username);
            }
        };

        const handleOAuthDisconnected = (event: CustomEvent) => {
            const { platform } = event.detail;
            if (platform === 'discord' && connected) {
                onDisconnect();
            }
        };

        window.addEventListener('oauth_connected', handleOAuthConnected as EventListener);
        window.addEventListener('oauth_disconnected', handleOAuthDisconnected as EventListener);

        return () => {
            window.removeEventListener('oauth_connected', handleOAuthConnected as EventListener);
            window.removeEventListener('oauth_disconnected', handleOAuthDisconnected as EventListener);
        };
    }, [onConnect, onDisconnect, connected]);

    const handleConnect = async () => {
        if (!oauthService.isPlatformConfigured('discord')) {
            toast.error("Discord OAuth is not configured. Please check your environment variables.");
            return;
        }

        try {
            await connect('discord');
        } catch (error) {
            toast.error("Failed to connect to Discord.");
        }
    };

    const handleDisconnect = () => {
        persistDisconnect('discord');
        onDisconnect();
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <Label htmlFor="discord" className="flex items-center gap-2">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" />
                    </svg>
                    Discord
                </Label>
                <a
                    href="https://discord.gg/KNKfUWqe3j"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-indigo-400 hover:text-indigo-300 flex items-center gap-1 transition-colors"
                >
                    Join Server <ExternalLink className="w-3 h-3" />
                </a>
            </div>

            {connected ? (
                <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-custom-paddings">
                        <button
                            onClick={() => window.open(`https://discord.com`, '_blank')}
                            className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity"
                        >
                            <Avatar className="w-8 h-8">
                                <AvatarImage src={discordProfile?.avatarUrl} alt={discordProfile?.displayName} />
                                <AvatarFallback>
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" />
                                    </svg>
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 text-left">
                                <p className="font-medium text-sm flex items-center gap-1">
                                    {discordProfile?.displayName || "Discord User"}

                                    <Badge variant="outline" className="whitespace-nowrap h-4 text-[10px] px-1 font-normal border-indigo-500/30 text-indigo-500">
                                        Connected
                                    </Badge>
                                    {userType && (
                                        <Badge variant="outline" className="whitespace-nowrap h-4 text-[10px] px-1 font-normal border-purple-500/30 text-purple-500">
                                            {userType.charAt(0).toUpperCase() + userType.slice(1)}
                                        </Badge>
                                    )}
                                </p>
                                <TooltipProvider delayDuration={0}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="text-[10px] text-muted-foreground flex items-center gap-1 cursor-help hover:text-foreground transition-colors">
                                                <Info className="w-3 h-3" />
                                                <span>How to get your role?</span>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" className="p-3 text-xs max-w-[250px]">
                                            <p className="font-semibold mb-1.5">Claim your role:</p>
                                            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                                                <li>Join our Discord server</li>
                                                <li>Click on <strong>Server Name</strong> (top-left) &gt; <strong>Linked Roles</strong></li>
                                                <li>Select <strong>IndieKeys</strong> to claim your role!</li>
                                            </ol>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        </button>
                        <div className="flex gap-2">
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={handleDisconnect}
                                disabled={loading}
                                className="h-8 px-2"
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                        Connect your Discord account for interactions and linked roles
                    </p>
                    {oauthService.isPlatformConfigured('discord') ? (
                        <Button
                            onClick={handleConnect}
                            disabled={isConnecting || loading}
                            variant="outline"
                            className="w-full"
                        >
                            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" />
                            </svg>
                            {isConnecting ? "Connecting..." : "Connect Discord"}
                        </Button>
                    ) : (
                        <div className="text-center p-4 border rounded-md border-yellow-500/20 bg-yellow-500/5">
                            <p className="text-sm text-yellow-600 mb-2 font-medium">
                                Discord OAuth not configured
                            </p>
                            <p className="text-[10px] text-muted-foreground">
                                Check VITE_DISCORD_CLIENT_ID and Client Secret
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
